import React, { memo, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import Amount from 'design-system/Amount';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import BlurAmountView from 'design-system/BlurAmountView';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { selectBalancesForAccount, selectScrambled } from 'reducers/selectors';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount } from 'utils/formatting';
import { getBalance } from 'utils/api/getBalance';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { CurrencyAmountText, IconArrow } from 'design-system';
import LogoBankAccount from 'design-system/LogoBankAccount';
import TouchableBounce from 'utils/packages/TouchableBounce';
import IconTrueBalance from 'design-system/icons/IconTrueBalance';
import { selectShowTrueBalance } from 'features/accounts/selectors';
const AccountHeaderBalance = ({ account }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const scrambled = useAppSelector(selectScrambled);
    const showTrueBalance = useAppSelector(selectShowTrueBalance);
    const { balance, currency, trueBalance } = useAppSelector((store) => selectBalancesForAccount(store, account));
    const { isUnlockedForSpace: isTrueBalanceUnlocked } = useShouldShowUpgrade({
        benefitId: 'trueBalance',
    });
    const onTrueBalancePress = useCallback(() => {
        navigation.navigate('TrueBalance', { id: account.id });
    }, [account.id]);
    const { nativeBalance } = account;
    const [, convertedBalance] = getBalance(account);
    let mainBalance = balance;
    let convertedAmount = convertedBalance?.amount;
    if (account.type === 'CREDITCARD') {
        if (typeof nativeBalance?.creditCardBalance === 'number') {
            mainBalance = nativeBalance.creditCardBalance;
        }
        else if (typeof account.creditCardBalance === 'number') {
            mainBalance = account.creditCardBalance;
        }
        convertedAmount = account.creditCardBalance;
    }
    if (account.type === 'LOAN' || account.type === 'MORTGAGE') {
        mainBalance = nativeBalance
            ? nativeBalance.postedBalance
            : account.postedBalance;
        convertedAmount = account.postedBalance;
    }
    const subtitle = useMemo(() => {
        const overdraftLimit = nativeBalance?.overdraftLimit
            ? nativeBalance.overdraftLimit
            : account.overdraftLimit;
        if (account.type === 'CHECKING' && typeof overdraftLimit === 'number') {
            return (<View style={styles.subtitle}>
          <Text TextThin-16 Secondary>
            Overdraft{' '}
          </Text>
          <BlurAmountView enabled={scrambled}>
            <Text NumbersThin-16 Secondary style={styles.numberMargin}>
              {formatAmount(overdraftLimit, currency, 2)}
            </Text>
          </BlurAmountView>
        </View>);
        }
        const availableBalance = nativeBalance
            ? nativeBalance.availableBalance
            : account.availableBalance;
        const creditLimit = nativeBalance
            ? nativeBalance.creditLimit
            : account.creditLimit;
        if ((account.type === 'CREDITCARD' ||
            account.type === 'LOAN' ||
            account.type === 'MORTGAGE') &&
            (typeof availableBalance === 'number' || typeof creditLimit === 'number')) {
            return (<View style={styles.subtitle}>
          {typeof availableBalance === 'number' && (<>
              <Text TextThin-16 Secondary>
                Available{' '}
              </Text>
              <BlurAmountView enabled={scrambled}>
                <Text NumbersThin-16 Secondary style={styles.numberMargin}>
                  {formatAmount(availableBalance, currency, 2)}
                </Text>
              </BlurAmountView>
            </>)}
          {typeof availableBalance === 'number' &&
                    typeof creditLimit === 'number' && <Text TextThin-16> </Text>}
          {typeof creditLimit === 'number' && (<>
              <Text TextThin-16 Secondary>
                Limit{' '}
              </Text>
              <BlurAmountView enabled={scrambled}>
                <Text NumbersThin-16 Secondary style={styles.numberMargin}>
                  {formatAmount(creditLimit, currency, 2)}
                </Text>
              </BlurAmountView>
            </>)}
        </View>);
        }
        if (account.type === 'CREDITCARD' ||
            account.type === 'LOAN' ||
            account.type === 'MORTGAGE' ||
            account.type === 'PENSION') {
            return (<Text TextThin-16 Secondary>
          Balance
        </Text>);
        }
        return (<Text TextThin-16 Secondary>
        Available
      </Text>);
    }, [
        account.availableBalance,
        account.creditLimit,
        account.overdraftLimit,
        account.type,
        currency,
        nativeBalance,
        scrambled,
        styles.numberMargin,
        styles.subtitle,
    ]);
    return (<View style={styles.container}>
      <LogoBankAccount size="R64" data={account} onCard/>
      <Spacer.H8 />
      <CurrencyAmountText size="XL" value={{ amount: mainBalance, currency }}/>
      {convertedBalance && typeof convertedAmount === 'number' ? (<Amount Numbers-16 Secondary value={{
                amount: convertedAmount,
                currency: convertedBalance.currency,
            }}/>) : null}
      <Spacer.H8 />
      {subtitle}
      {account.type === 'CHECKING' &&
            showTrueBalance &&
            isTrueBalanceUnlocked ? (<TouchableBounce onPress={onTrueBalancePress} style={styles.trueBalance}>
          <IconTrueBalance color={colors.text.textOnBrand} width={20} height={20}/>
          <View style={styles.subtitle}>
            <Text TextThin-16 OnBrand>
              True balance{' '}
            </Text>
            <BlurAmountView enabled={scrambled}>
              <Text NumbersThin-16 OnBrand style={styles.numberMargin}>
                {formatAmount(trueBalance, currency, 2)}
              </Text>
            </BlurAmountView>
            <Spacer w={14}/>
            <IconArrow direction="right" width={8} color={colors.text.textOnBrand}/>
          </View>
        </TouchableBounce>) : null}
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        paddingTop: rem(32),
        paddingBottom: rem(40),
        alignItems: 'center',
    },
    subtitle: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
    },
    numberMargin: {
        marginTop: 1,
    },
    trueBalance: {
        marginTop: rem(32),
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(8),
        backgroundColor: colors.elements.brandLight,
        borderRadius: rem(42),
        paddingHorizontal: rem(16),
        paddingVertical: rem(8),
    },
}));
export default memo(AccountHeaderBalance);
